// Contrato entre la lógica de captura y su interfaz (el Web Component o un doble de pruebas).
// La lógica describe pantallas; la interfaz las pinta y devuelve las acciones del usuario.
import type { ChallengeDisplay } from './ChallengeRunner.js';
import type { UploadProgress } from './Uploader.js';
import type { CaptureHandoff, CaptureResult, CaptureStep } from './types.js';

export type ViewName = 'front' | 'side' | 'top' | 'label_corner';
export const VIEWS: readonly ViewName[] = ['front', 'side', 'top', 'label_corner'];

export type QualityWarning = 'dark' | 'blurry';

/** Motivos de `rejected` antes de abrir sesión (guardián, permisos, navegador, servidor). */
export type RejectReason =
  | 'virtual_camera'
  | 'settings_incoherent'
  | 'getusermedia_patched'
  | 'flat_frame'
  | 'no_sensor_noise'
  | 'no_frames'
  | 'no_camera'
  | 'permission_denied'
  | 'unsupported'
  | 'server';

export type Screen =
  | { kind: 'loading' }
  | { kind: 'permission'; checking: boolean }
  | {
      kind: 'label';
      tracking?: string;
      detected?: string;
      busy: boolean;
      warning?: QualityWarning;
    }
  | { kind: 'challenge'; phase: 'intro' | 'running' | 'done' }
  | {
      kind: 'view';
      view: ViewName;
      n: number;
      total: number;
      busy: boolean;
      warning?: QualityWarning;
    }
  | { kind: 'ruler'; rulerUrl?: string }
  | { kind: 'declarations'; tracking?: string }
  | {
      kind: 'summary';
      shots: number;
      chunks: number;
      phase: 'ready' | 'uploading' | 'done';
      upload?: UploadProgress;
      result?: CaptureResult;
    }
  | { kind: 'rejected'; reason: RejectReason }
  | {
      kind: 'handoff';
      handoff: CaptureHandoff;
      status: 'waiting' | 'opened' | 'expired' | 'done';
      step?: CaptureStep;
      percent?: number;
      result?: CaptureResult;
    }
  | { kind: 'error' };

/** Acción del usuario. `primary` es el botón principal (disparar, empezar, continuar, enviar). */
export type UIAction =
  { type: 'primary'; values?: Record<string, string> } | { type: 'retry' } | { type: 'skip' };

/** Interfaz de la captura. */
export interface CaptureUI {
  /** Pinta una pantalla (sustituye a la anterior; conserva lo tecleado si es la misma). */
  render(screen: Screen): void;
  /** Próxima acción del usuario (cada llamada sustituye a la anterior pendiente). */
  action(): Promise<UIAction>;
  /** Muestra la vista previa del stream; devuelve el `<video>` (o `null` sin DOM). */
  preview(stream: MediaStream | null): HTMLVideoElement | null;
  /** Superficie del reto de luz. */
  challengeDisplay(): ChallengeDisplay;
  /** Rellena un campo de la pantalla actual (p. ej. el tracking detectado). */
  fill?(name: string, value: string): void;
  destroy(): void;
}
